import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { prepararAvatar } from "@/lib/imagenAvatar";

/**
 * Alta, cambio y baja de la foto de perfil de un usuario.
 *
 * La foto vive en el bucket público `avatar`, bajo `avatars/<correo>/`, y el
 * puntero se guarda en `usuarios.foto_perfil_url`. Es el mismo par que ya usan el
 * Portal Agente y el Portal Cliente, así que una foto subida aquí se ve en todos.
 *
 * Dos cosas que el bucket impone y conviene validar ANTES de subir, porque si no
 * el error que regresa Storage es opaco:
 *   - `allowed_mime_types` = jpeg, png y webp. El HEIC del iPhone NO entra.
 *   - `file_size_limit` = 5 MB.
 *
 * La imagen NO se sube tal cual: `prepararAvatar` la reescala a 512 px antes de
 * mandarla. Además de que un avatar no necesita más, el proxy de Preview corta el
 * cuerpo de la petición en 1 MB y devuelve un 413 sin cabeceras CORS, que en el
 * navegador se ve como un opaco "Failed to fetch". Ver `lib/imagenAvatar`.
 *
 * El archivo se nombra con marca de tiempo (`avatar-<epoch>.<ext>`) a propósito:
 * la URL pública se guarda sin query string (ver `normalizeAvatarUrl`), así que
 * reescribir siempre la misma ruta dejaría al navegador mostrando la foto vieja
 * desde su caché. Con un nombre nuevo la URL cambia y la imagen se refresca sola.
 * Los archivos anteriores de esa carpeta se borran después de guardar el puntero.
 */

/**
 * Formatos que se aceptan de ENTRADA. Se limitan a los que todos los navegadores
 * saben decodificar en un canvas: lo que finalmente se sube siempre es el WebP
 * (o JPG) que produce `prepararAvatar`, así que el bucket recibe un tipo válido
 * venga de donde venga.
 */
const TIPOS_PERMITIDOS = ["image/jpeg", "image/png", "image/webp"] as const;
/**
 * Tope del archivo de origen. No es el límite del bucket (5 MB) ni el del proxy
 * (1 MB): como la imagen se reescala antes de subirla, aquí sólo se cuida no
 * meter al canvas algo que tumbe la pestaña.
 */
const TAMANO_MAXIMO = 20 * 1024 * 1024;
const BUCKET = "avatar";

/** Lo que acepta el `<input type="file">`. */
export const FOTO_ACCEPT = TIPOS_PERMITIDOS.join(",");
export const FOTO_FORMATOS_TEXTO = "JPG, PNG o WebP - se ajusta sola a 512 px";

/** Carpeta del usuario dentro del bucket. */
const carpetaDe = (email: string) => `avatars/${email}`;

/**
 * Valida contra las reglas del bucket. Devuelve el motivo del rechazo o `null`
 * si el archivo sirve.
 */
export function validarFotoPerfil(file: File): string | null {
  if (!(TIPOS_PERMITIDOS as readonly string[]).includes(file.type)) {
    const esHeic = /heic|heif/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
    return esHeic
      ? "Las fotos HEIC del iPhone no se aceptan. Expórtala como JPG o toma la captura de pantalla."
      : `Formato no admitido. Usa ${FOTO_FORMATOS_TEXTO}.`;
  }
  if (file.size > TAMANO_MAXIMO) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    return `La imagen pesa ${mb} MB y es demasiado grande para procesarla. Usa una de menos de 20 MB.`;
  }
  return null;
}

/**
 * Texto legible de un error de Storage o PostgREST. Estos objetos traen el motivo
 * en campos distintos según de dónde vengan (`message`, `error`, `statusCode`), y
 * quedarse con un mensaje genérico esconde justo lo que hace falta para arreglarlo.
 */
function motivo(err: any, porDefecto: string): string {
  const partes = [err?.message, err?.error, err?.details, err?.hint]
    .filter((v) => typeof v === "string" && v.trim())
    .filter((v, i, arr) => arr.indexOf(v) === i);
  const codigo = err?.statusCode ?? err?.status ?? err?.code;
  const texto = partes.join(" - ") || porDefecto;
  return codigo ? `${texto} (${codigo})` : texto;
}

/** Ruta dentro del bucket a partir de la URL pública guardada. */
function rutaDesdeUrl(publicUrl: string): string | null {
  const marca = `/storage/v1/object/public/${BUCKET}/`;
  const i = publicUrl.indexOf(marca);
  if (i === -1) return null;
  return publicUrl.slice(i + marca.length).split("?")[0] ?? null;
}

/**
 * Borra todo lo que haya en la carpeta salvo `conservar`.
 *
 * Nunca propaga el error: para cuando esto corre, el puntero de la foto ya quedó
 * guardado. Dejar un archivo huérfano en el bucket es un costo menor comparado
 * con decirle al usuario que falló un guardado que sí funcionó.
 */
async function limpiarCarpeta(email: string, conservar?: string) {
  try {
    const carpeta = carpetaDe(email);
    const { data: archivos } = await supabase.storage.from(BUCKET).list(carpeta);
    const sobrantes = (archivos ?? [])
      .map((f) => `${carpeta}/${f.name}`)
      .filter((ruta) => ruta !== conservar);
    if (sobrantes.length) await supabase.storage.from(BUCKET).remove(sobrantes);
  } catch (err) {
    console.warn("[foto-perfil] no se pudieron retirar las fotos anteriores:", err);
  }
}

export interface FotoPerfilUsuario {
  subir: (file: File) => Promise<void>;
  eliminar: () => Promise<void>;
  subiendo: boolean;
  eliminando: boolean;
}

/**
 * @param email  Correo del usuario dueño de la foto, tal como está guardado en
 *   `usuarios.email` (es su llave). Debe venir de la propia tabla, no del texto
 *   que escribió alguien: el filtro es por igualdad exacta a propósito —`ilike`
 *   trataría el guion bajo de un correo como comodín y podría tocar otra fila.
 * @param onCambio  Se llama tras guardar para que la pantalla refresque su query.
 */
export function useFotoPerfilUsuario(
  email: string | null | undefined,
  onCambio?: () => void,
): FotoPerfilUsuario {
  const { profile, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const [subiendo, setSubiendo] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  const correo = (email ?? "").trim();
  /** Sólo tiene caso refrescar la sesión si la foto es la del propio usuario. */
  const esPropio = correo.toLowerCase() === (profile?.email ?? "").trim().toLowerCase();

  /**
   * Refresco posterior al guardado. Como la limpieza del bucket, no propaga:
   * a esta altura el cambio ya está en la base, y fallar aquí haría que la
   * pantalla reportara un error sobre algo que sí se guardó.
   */
  const trasGuardar = useCallback(async () => {
    try {
      queryClient.invalidateQueries({ queryKey: ["portal-personal-perfil"] });
      if (esPropio) await refreshProfile();
      onCambio?.();
    } catch (err) {
      console.warn("[foto-perfil] la foto se guardó, pero falló el refresco:", err);
    }
  }, [queryClient, esPropio, refreshProfile, onCambio]);

  /** URL actual, leída al momento: evita depender de un estado que puede venir stale. */
  const urlActual = useCallback(async (): Promise<string | null> => {
    const { data } = await (supabase as any)
      .from("usuarios")
      .select("foto_perfil_url")
      .eq("email", correo)
      .limit(1);
    return ((data as any[]) ?? [])[0]?.foto_perfil_url ?? null;
  }, [correo]);

  const subir = useCallback(
    async (file: File) => {
      if (!correo) throw new Error("No hay un correo al cual asociar la foto.");
      const invalido = validarFotoPerfil(file);
      if (invalido) throw new Error(invalido);

      setSubiendo(true);
      try {
        // Se sube la versión reescalada, nunca el archivo original.
        const imagen = await prepararAvatar(file);
        const ruta = `${carpetaDe(correo)}/avatar-${Date.now()}.${imagen.extension}`;

        const { error: errSubida } = await supabase.storage
          .from(BUCKET)
          .upload(ruta, imagen.blob, {
            upsert: true,
            cacheControl: "3600",
            contentType: imagen.tipo,
          });
        if (errSubida) {
          console.error("[foto-perfil] falló la subida al bucket:", { ruta, errSubida });
          throw new Error(
            `No se pudo subir la imagen: ${motivo(errSubida, "Storage rechazó el archivo.")}`,
          );
        }

        const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(ruta);
        // Supabase agrega `?t=` al hacer upsert; la URL se guarda limpia.
        const url = urlData.publicUrl.split("?")[0]!;

        // `.select()` para distinguir "no se pudo por permisos" de "no pasó nada".
        const { data: filas, error: errUpdate } = await (supabase as any)
          .from("usuarios")
          .update({ foto_perfil_url: url })
          .eq("email", correo)
          .select("email");
        if (errUpdate || !filas?.length) {
          console.error("[foto-perfil] falló el guardado del puntero:", {
            correo,
            errUpdate,
            filas,
          });
          // El puntero no quedó guardado: no dejar el archivo suelto en el bucket.
          await supabase.storage.from(BUCKET).remove([ruta]);
          throw new Error(
            errUpdate
              ? `No se pudo guardar la foto: ${motivo(errUpdate, "la base rechazó el cambio.")}`
              : `No se pudo guardar la foto: ninguna cuenta coincide con ${correo} o no tienes permiso para editarla.`,
          );
        }

        // Ya con el puntero a salvo, se retiran las fotos anteriores.
        await limpiarCarpeta(correo, ruta);
        await trasGuardar();
      } finally {
        setSubiendo(false);
      }
    },
    [correo, trasGuardar],
  );

  const eliminar = useCallback(async () => {
    if (!correo) return;
    setEliminando(true);
    try {
      // La ruta se lee ANTES de limpiar la columna: después ya no habría de dónde.
      const anterior = await urlActual();

      const { data: filas, error: errUpdate } = await (supabase as any)
        .from("usuarios")
        .update({ foto_perfil_url: null })
        .eq("email", correo)
        .select("email");
      if (errUpdate || !filas?.length) {
        console.error("[foto-perfil] falló la baja de la foto:", { correo, errUpdate, filas });
        throw new Error(
          errUpdate
            ? `No se pudo eliminar la foto: ${motivo(errUpdate, "la base rechazó el cambio.")}`
            : `No se pudo eliminar la foto: ninguna cuenta coincide con ${correo} o no tienes permiso para editarla.`,
        );
      }

      // Se borra la ruta guardada y, por si quedó algo de subidas previas, la carpeta.
      const ruta = anterior ? rutaDesdeUrl(anterior) : null;
      if (ruta) await supabase.storage.from(BUCKET).remove([ruta]);
      await limpiarCarpeta(correo);

      await trasGuardar();
    } finally {
      setEliminando(false);
    }
  }, [correo, urlActual, trasGuardar]);

  return { subir, eliminar, subiendo, eliminando };
}
